<script setup lang="ts">
// Browsing an end-to-end encrypted git repository (HRTH-114): pick a branch or tag, walk
// its files, read them (highlighted; READMEs rendered), see history per branch or file,
// and any commit's diff. Everything is decrypted and read on this device (lib/git.ts).
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue'
import { describeError } from '../api/http'
import { decryptRefs, defaultBranch, isText, loadPacks, Repository, shortSha, subject, type GitCommit, type GitFileDiff, type GitTreeEntry, type RefTarget } from '../lib/git'
import { highlightLines, renderMarkdown } from '../lib/render'
import { formatBytes } from '../lib/pattern'
import type { OpenLibrary } from '../sync/engine'
import PullRequests from './PullRequests.vue'
import BranchProtection from './BranchProtection.vue'
import type { RefProtection } from '../api/types'
import { blindRef } from '../lib/metacrypto'
import { usePulls } from '../stores/pulls'
import { useSession } from '../stores/session'

const props = defineProps<{ library: OpenLibrary; cloneUrl: string }>()
const pulls = usePulls()
const session = useSession()

type View = { mode: 'tree' | 'file' | 'history'; path: string } | { mode: 'commit'; id: string }

const repo = shallowRef<Repository | null>(null)
const refs = ref<RefTarget[]>([])
const current = ref<string>('')
const view = ref<View>({ mode: 'tree', path: '' })
const loading = ref(true)
/** Browsing the code, or its pull requests. */
const section = ref<'code' | 'pulls'>('code')
/** Protected branches: the rules (by blinded id), and the names this device can match. */
const protection = ref<{ rules: RefProtection[]; canManage: boolean; names: Map<string, string> }>({ rules: [], canManage: false, names: new Map() })
const protectedNames = computed(() => new Set(protection.value.rules.map((r) => protection.value.names.get(r.ref_id)).filter(Boolean) as string[]))
const blind = (name: string) => blindRef(session.libraryKey(props.library.id) ?? '', props.library.id, name)

async function loadProtection(): Promise<void> {
  try {
    const p = await session.api.refProtections(props.library.id)
    const names = new Map<string, string>()
    for (const r of refs.value) names.set(await blind(r.name), r.name)
    protection.value = { rules: p.rules, canManage: p.can_manage, names }
  } catch {
    // Not fatal: branches just aren't marked.
  }
}
const error = ref<string | null>(null)

const head = computed(() => refs.value.find((r) => r.name === current.value)?.sha ?? '')
const branches = computed(() => refs.value.filter((r) => r.name.startsWith('refs/heads/')))
const tags = computed(() => refs.value.filter((r) => r.name.startsWith('refs/tags/')))
const shortName = (name: string) => name.replace(/^refs\/(heads|tags)\//, '')

async function load(): Promise<void> {
  loading.value = true
  error.value = null
  const key = session.libraryKey(props.library.id)
  try {
    if (!key) throw new Error('This device doesn’t have this repository’s key yet.')
    refs.value = await decryptRefs(key, props.library.id, (await session.api.repoRefs(props.library.id)).refs)
    const r = new Repository()
    await loadPacks(session.api, props.library.id, key, r, session.storageHandle())
    repo.value?.free()
    repo.value = r
    if (!refs.value.some((x) => x.name === current.value)) current.value = defaultBranch(refs.value)?.name ?? refs.value[0]?.name ?? ''
    await loadProtection()
  } catch (e) {
    error.value = describeError(e)
  } finally {
    loading.value = false
  }
}

watch(() => props.library.id, () => {
  view.value = { mode: 'tree', path: '' }
  current.value = ''
  void load()
}, { immediate: true })
onBeforeUnmount(() => repo.value?.free())

// --- What's shown --------------------------------------------------------------------

const crumbs = computed(() => {
  if (view.value.mode === 'commit') return []
  const parts = view.value.path.split('/').filter(Boolean)
  return parts.map((name, i) => ({ name, path: parts.slice(0, i + 1).join('/') }))
})

const entries = computed<GitTreeEntry[]>(() => {
  const r = repo.value
  if (!r || !head.value || view.value.mode !== 'tree') return []
  try {
    const e = r.entryAt(head.value, view.value.path)
    return e.kind === 'dir' ? r.tree(e.id) : []
  } catch {
    return []
  }
})

const readme = ref<string | null>(null)
const fileLines = ref<string[] | null>(null)
const fileHtml = ref<string | null>(null)
const fileInfo = ref<{ size: number; text: boolean } | null>(null)

function readBlob(path: string): Uint8Array | null {
  const r = repo.value
  if (!r || !head.value) return null
  try {
    const e = r.entryAt(head.value, path)
    return e.kind === 'file' || e.kind === 'symlink' ? r.blob(e.id) : null
  } catch {
    return null
  }
}

watch([view, head, repo], async () => {
  readme.value = fileLines.value = fileHtml.value = null
  fileInfo.value = null
  const v = view.value
  if (v.mode === 'tree') {
    const md = entries.value.find((e) => e.kind === 'file' && /^readme(\.md|\.markdown)?$/i.test(e.name))
    const bytes = md && readBlob(v.path ? `${v.path}/${md.name}` : md.name)
    if (bytes && isText(bytes)) readme.value = await renderMarkdown(new TextDecoder().decode(bytes))
  } else if (v.mode === 'file') {
    const bytes = readBlob(v.path)
    if (!bytes) return
    fileInfo.value = { size: bytes.length, text: isText(bytes) }
    if (!fileInfo.value.text || bytes.length > 2 * 1024 * 1024) return
    const text = new TextDecoder().decode(bytes)
    if (/\.(md|markdown)$/i.test(v.path)) fileHtml.value = await renderMarkdown(text)
    else fileLines.value = await highlightLines(text, v.path.split('/').pop() ?? '')
  }
})

const history = computed<GitCommit[]>(() => {
  const r = repo.value
  if (!r || !head.value || view.value.mode !== 'history') return []
  try {
    return r.log(head.value, 300, view.value.path || undefined)
  } catch {
    return []
  }
})

const commit = computed<{ c: GitCommit; files: GitFileDiff[] } | null>(() => {
  const r = repo.value
  if (!r || view.value.mode !== 'commit') return null
  try {
    return { c: r.commit(view.value.id), files: r.diffCommit(view.value.id) }
  } catch {
    return null
  }
})

function open(e: GitTreeEntry): void {
  if (view.value.mode !== 'tree') return
  const path = view.value.path ? `${view.value.path}/${e.name}` : e.name
  view.value = e.kind === 'dir' ? { mode: 'tree', path } : { mode: 'file', path }
}

function download(path: string): void {
  const bytes = readBlob(path)
  if (!bytes) return
  const href = URL.createObjectURL(new Blob([new Uint8Array(bytes)]))
  const a = document.createElement('a')
  a.href = href
  a.download = path.split('/').pop() ?? 'file'
  a.click()
  setTimeout(() => URL.revokeObjectURL(href), 60_000)
}

const when = (t: number) => new Date(t * 1000).toLocaleString()

function patchLines(patch: string): { text: string; cls: string }[] {
  return patch
    .split('\n')
    .filter((l) => !l.startsWith('---') && !l.startsWith('+++'))
    .map((text) => ({
      text,
      cls: text.startsWith('@@') ? 'text-text-muted' : text.startsWith('+') ? 'diff-add' : text.startsWith('-') ? 'diff-del' : '',
    }))
}
</script>

<template>
  <section class="px-6 pt-4" data-testid="repo-browser">
    <!-- Only the first load replaces the view: reloads (after a merge) keep what is open. -->
    <p v-if="loading && !repo" class="mono text-text-muted">Decrypting the repository on this device…</p>
    <p v-else-if="error" class="mono text-signal" data-testid="repo-error">{{ error }}</p>
    <div v-else-if="refs.length === 0" class="mono text-text-muted" data-testid="repo-empty">
      Nothing pushed yet. Push with <code class="select-all">git push {{ cloneUrl }} main</code>.
    </div>

    <template v-else>
      <div class="mono mb-3 flex gap-4 border-b border-border pb-2 uppercase">
        <button :class="section === 'code' ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'" data-testid="repo-section-code" @click="section = 'code'">Code</button>
        <button :class="section === 'pulls' ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'" data-testid="repo-section-pulls" @click="section = 'pulls'">
          Pull requests<span v-if="pulls.byLibrary[library.id]" class="badge ml-2" data-testid="repo-pulls-unread">{{ pulls.byLibrary[library.id] }}</span>
        </button>
      </div>
      <PullRequests v-if="section === 'pulls' && repo" :library="library" :repo="repo" :refs="refs" :protected-branches="protectedNames" @changed="load" />
      <template v-else>
      <BranchProtection
        :library="library.id"
        :rules="protection.rules"
        :can-manage="protection.canManage"
        :names="protection.names"
        :branches="branches"
        :blind="blind"
        @changed="loadProtection"
      />
      <!-- Branch, where we are, and Files / History -->
      <div class="mb-3 flex flex-wrap items-center gap-3">
        <select v-model="current" class="input w-auto" aria-label="Branch or tag" data-testid="repo-ref">
          <optgroup label="Branches">
            <option v-for="b in branches" :key="b.name" :value="b.name">{{ protectedNames.has(b.name) ? '🔒 ' : '' }}{{ shortName(b.name) }}</option>
          </optgroup>
          <optgroup v-if="tags.length" label="Tags">
            <option v-for="t in tags" :key="t.name" :value="t.name">{{ shortName(t.name) }}</option>
          </optgroup>
        </select>
        <nav class="mono flex min-w-0 flex-wrap items-center gap-1 text-text-muted" aria-label="Path">
          <button class="hover:text-text-primary" data-testid="repo-root" @click="view = { mode: 'tree', path: '' }">{{ library.name }}</button>
          <template v-for="c in crumbs" :key="c.path">
            <span>/</span>
            <button class="hover:text-text-primary" @click="view = { mode: 'tree', path: c.path }">{{ c.name }}</button>
          </template>
        </nav>
        <span class="flex-1" />
        <button
          class="mono uppercase"
          :class="view.mode === 'history' ? 'text-text-primary' : 'text-text-muted hover:text-text-primary'"
          data-testid="repo-history"
          @click="view = { mode: 'history', path: view.mode === 'commit' ? '' : view.path }"
        >
          History
        </button>
      </div>

      <!-- Folder -->
      <template v-if="view.mode === 'tree'">
        <div class="border-t border-border" data-testid="repo-tree">
          <button
            v-for="e in entries"
            :key="e.name"
            class="flex w-full items-center gap-3 border-b border-border py-1.5 text-left text-sm hover:bg-bg-hover"
            :data-testid="`repo-entry-${e.name}`"
            @click="open(e)"
          >
            <span class="mono w-8 shrink-0 text-text-muted">{{ e.kind === 'dir' ? 'DIR' : e.kind === 'symlink' ? 'LNK' : e.kind === 'submodule' ? 'MOD' : '' }}</span>
            <span class="truncate">{{ e.name }}{{ e.kind === 'dir' ? '/' : '' }}</span>
          </button>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -- sanitised by renderMarkdown -->
        <article v-if="readme" class="md mt-6 max-w-3xl border border-border p-6" data-testid="repo-readme" v-html="readme" />
      </template>

      <!-- File -->
      <template v-else-if="view.mode === 'file'">
        <div class="mono mb-2 flex items-center gap-4 text-text-muted">
          <span>{{ fileInfo ? formatBytes(fileInfo.size) : '' }}</span>
          <span class="flex-1" />
          <button class="uppercase hover:text-text-primary" @click="view = { mode: 'history', path: view.path }">File history</button>
          <button class="uppercase hover:text-text-primary" data-testid="repo-download" @click="download(view.path)">Download</button>
        </div>
        <!-- eslint-disable-next-line vue/no-v-html -- sanitised by renderMarkdown -->
        <article v-if="fileHtml" class="md max-w-3xl border border-border p-6" v-html="fileHtml" />
        <div v-else-if="fileLines" class="code-view scroll-thin overflow-x-auto border border-border" data-testid="repo-file">
          <table class="code-text w-full border-collapse text-[13px] leading-5">
            <tr v-for="(l, i) in fileLines" :key="i">
              <td class="w-12 select-none border-r border-border px-2 text-right align-top text-text-disabled">{{ i + 1 }}</td>
              <!-- eslint-disable-next-line vue/no-v-html -- highlight.js escapes its input -->
              <td class="whitespace-pre px-3" v-html="l || ' '" />
            </tr>
          </table>
        </div>
        <p v-else-if="fileInfo && !fileInfo.text" class="mono text-text-muted">Binary file. Download it to open it.</p>
        <p v-else-if="fileInfo" class="mono text-text-muted">Too large to show here. Download it to open it.</p>
      </template>

      <!-- History -->
      <template v-else-if="view.mode === 'history'">
        <h2 class="label">History{{ view.path ? ` of ${view.path}` : '' }} · {{ String(history.length).padStart(3, '0') }}</h2>
        <div class="border-t border-border" data-testid="repo-log">
          <button
            v-for="c in history"
            :key="c.id"
            class="grid w-full grid-cols-[5rem_1fr_10rem] items-baseline gap-3 border-b border-border py-2 text-left text-sm hover:bg-bg-hover max-sm:grid-cols-[5rem_1fr]"
            :data-testid="`repo-commit-${shortSha(c.id)}`"
            @click="view = { mode: 'commit', id: c.id }"
          >
            <span class="mono text-text-muted">{{ shortSha(c.id) }}</span>
            <span class="min-w-0 truncate">{{ subject(c) }} <span class="text-text-muted">· {{ c.author.name }}</span></span>
            <span class="mono text-right text-text-muted max-sm:hidden">{{ when(c.committer.time) }}</span>
          </button>
        </div>
      </template>

      <!-- Commit -->
      <template v-else-if="commit">
        <div class="mb-4 border border-border p-4" data-testid="repo-commit">
          <p class="whitespace-pre-wrap">{{ commit.c.message.trim() }}</p>
          <p class="mono mt-2 text-text-muted">
            {{ commit.c.author.name }} · {{ when(commit.c.author.time) }} · {{ shortSha(commit.c.id) }}
            <template v-for="p in commit.c.parents" :key="p">
              · parent <button class="underline hover:text-text-primary" @click="view = { mode: 'commit', id: p }">{{ shortSha(p) }}</button>
            </template>
          </p>
        </div>
        <div v-for="f in commit.files" :key="f.path" class="mb-4 border border-border" :data-testid="`repo-diff-${f.path}`">
          <div class="mono flex items-center gap-3 border-b border-border px-3 py-1.5 text-text-muted">
            <span class="uppercase">{{ f.change }}</span>
            <span class="min-w-0 flex-1 truncate text-text-primary">{{ f.path }}</span>
            <span v-if="!f.binary">+{{ f.additions }} −{{ f.deletions }}</span>
          </div>
          <p v-if="f.binary" class="mono px-3 py-2 text-text-muted">Binary file.</p>
          <p v-else-if="!f.patch" class="mono px-3 py-2 text-text-muted">Too large to show.</p>
          <pre v-else class="mono scroll-thin overflow-x-auto text-[13px] leading-5"><div v-for="(l, i) in patchLines(f.patch)" :key="i" class="px-3" :class="l.cls">{{ l.text || ' ' }}</div></pre>
        </div>
      </template>
      </template>
    </template>
  </section>
</template>
